import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type ForgeUIExtensionType, type ForgeExtensionPoints } from '@atlassian/forge-ui-types';
import { bitbucketExtensionURLHelpers } from './navigation-targets/bitbucket';
import {
	confluenceExtensionURLHelpers,
	confluenceNavigationTargetURLHelpers,
} from './navigation-targets/confluence';
import { type TargetURLUtils, getModuleByKey, NavigationError } from './navigation-targets/common';
import { jiraExtensionURLHelpers, jiraNavigationTargetHandlers } from './navigation-targets/jira';
import { globalExtensionURLHelpers } from './navigation-targets/global';
import { ensureURLPath } from './createMakeProductURLPath';

type FullForgeUIExtensionType = ForgeUIExtensionType & {
	installationId?: string; // Added to support global navigation helpers
	properties: ForgeUIExtensionType['properties'] & {
		route?: string;
	};
};

const extensionURLHelpers = {
	...bitbucketExtensionURLHelpers,
	...confluenceExtensionURLHelpers,
	...jiraExtensionURLHelpers,
	...globalExtensionURLHelpers,
} as const;

const navigationTargetHandlers: {
	readonly contentEdit: ({ contentId }: { contentId: string }) => Promise<string>;
	readonly contentList: (
		params:
			| {
					contentType: 'page' | 'blogpost';
					spaceKey: string;
			  }
			| {
					contentType: 'customContent';
					moduleKey: string;
			  },
		{ getAppForgeUIExtensions }: TargetURLUtils,
	) => Promise<string>;
	readonly contentView: ({
		contentId,
		version,
	}: {
		contentId: string;
		version?: number;
	}) => Promise<string>;
	readonly dashboard: ({ dashboardId }: { dashboardId: string }) => Promise<string>;
	readonly issue: ({ issueKey }: { issueKey: string }) => Promise<string>;
	readonly module: (
		{ moduleKey, path, ...params }: { moduleKey?: string; path?: string } & Record<string, unknown>,
		{ getAppForgeUIExtensions }: TargetURLUtils,
	) => Promise<string | null>;
	readonly projectSettingsDetails: ({ projectKey }: { projectKey: string }) => Promise<string>;
	readonly spaceView: ({ spaceKey }: { spaceKey: string }) => Promise<string>;
	readonly userProfile: ({ accountId }: { accountId: string }) => Promise<string>;
} = {
	...jiraNavigationTargetHandlers,
	...confluenceNavigationTargetURLHelpers,
	userProfile: async ({ accountId }: { accountId: string }): Promise<string> =>
		ensureURLPath(`/people/${accountId}`),
	module: async (
		{ moduleKey, path, ...params }: { moduleKey?: string; path?: string } & Record<string, unknown>,
		{ getAppForgeUIExtensions }: TargetURLUtils,
	): Promise<string | null> => {
		if (!moduleKey) {
			throw NavigationError.MissingProperty('moduleKey');
		}

		const module = await getModuleByKey(getAppForgeUIExtensions, moduleKey);
		if (!module || !module.appId || !module.environmentId || !module.environmentKey) {
			throw NavigationError.ModuleNotFound(moduleKey);
		}

		const moduleUrlHelper = extensionURLHelpers[module.type as ForgeExtensionPoints];
		if (!moduleUrlHelper) {
			throw NavigationError.NotSupportedExtensionType(module.type as ForgeExtensionPoints);
		}
		const basePath = await moduleUrlHelper(module as FullForgeUIExtensionType, params);

		if (basePath && path && fg('forge-ui-navigate-module-path')) {
			return `${basePath}${path}`;
		}

		return basePath;
	},
} as const;

type NavigationTargetHandlers = typeof navigationTargetHandlers;
type NavigationTargetType = keyof NavigationTargetHandlers;

type FirstParameterType<T> = T extends (...args: infer P) => unknown
	? P extends [infer U, ...rest: unknown[]]
		? U
		: unknown
	: never;

type NavigateTargetInfo<T extends NavigationTargetType> = FirstParameterType<
	NavigationTargetHandlers[T]
> & {
	target: T;
};

type NavigationTargetHandler<T extends NavigationTargetType> = (
	data: Omit<NavigateTargetInfo<T>, 'target'>,
	utils: TargetURLUtils,
) => Promise<string | null>;

export const createNavigateTargetPath = async <T extends NavigationTargetType>(
	targetInfo: NavigateTargetInfo<T>,
	utils: TargetURLUtils,
): Promise<string | null> => {
	const { target, ...payload } = targetInfo;
	const handler = navigationTargetHandlers[target] as unknown as NavigationTargetHandler<T>;

	if (!handler) {
		return null;
	}
	try {
		const path = await handler(payload, utils);
		return path;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error((e as Error).message);
		return null;
	}
};
